<template>
  <div class="power-outage-chart">
    <h3>{{ title }}</h3>
    <div>
      <v-chart
        v-if="chartOptions"
        ref="chartInstance"
        @finished="chartFinished"
        :option="chartOptions"
        style="height: 230px; width: 95%;"
        @click="chartOnClick"
      />
    </div>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashMax from "lodash/max";
import lodashMin from "lodash/min";
import lodashCloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { GridComponent, TooltipComponent } from "echarts/components";

import { LineChart } from "echarts/charts";
use([CanvasRenderer, GridComponent, TooltipComponent, LineChart]);

import { todayUtc } from "@/common/date.utilities";

const EMITTED_ACTIONS = {
  CLICK: "click",
  FINISHED: "finished",
};

export default {
  name: "PowerOutageChart",
  props: {
    title: {
      type: String,
      required: false,
    },
    series: {
      type: Array,
      required: true,
    },
  },
  data: () => ({}),
  computed: {
    /**
     * @returns {object} An echarts Chart Options object
     */
    chartOptions() {
      return {
        backgroundColor: "rgba(0, 0, 0, 0)",
        color: [this.getColor("white")],
        legend: {
          show: false,
        },
        title: {
          show: false,
        },
        grid: {
          left: "12%",
          right: "5%",
          bottom: "15%",
          top: "5%",
        },
        animation: true,
        tooltip: {
          show: true,
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (params) => {
            let point;

            params.forEach((param) => {
              if (!(typeof param.value === "undefined")) {
                point = param;
              }
            });

            const COLOR = this.getColor(point.seriesName);
            const DOT = `
              <span style="display:inline-block;border-radius:10px;width:10px;height:10px;background-color:${COLOR};"></span>
            `;
            return `
              <span class="pr-1">${this.formatHoverDate(
                point.axisValue
              )} UTC</span>
              <div>
                <span class="pr-1">${DOT}</span>
                <span class="pr-1">${point.value}</span>
              <\div>
            `;
          },
          hideDelay: 1000,
          borderWidth: 0,
          backgroundColor: "#000",
          textStyle: {
            color: "#FFF",
          },
          appendToBody: true,
        },
        xAxis: {
          data: this.timeValues,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            show: true,
            interval: (index, value) => {
              //Display the x-axis label for the first, middle, and last date to keep things readable
              if (
                index === 0 ||
                index === this.series.length - 1 ||
                index === Math.round(this.series.length / 2)
              ) {
                return true;
              }
            },
            color: "white",
            formatter: (value) => {
              return this.formatDate(value);
            },
            inside: false,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: "value",
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            margin: 5,
            show: true,
            color: this.getColor("white"),
            showMaxLabel: true,
            formatter: (value) => {
              //Format zero as '0' or it will display as '0.00' which looks weird
              if (value === 0) return "0";
              //Round values to three decimal places because long values get cut off visually in the chart
              return value.toFixed(3);
            },
          },
          splitLine: {
            show: false,
          },
        },
        series: [this.impairedSeries, this.fluctuationSeries, this.emptySeries],
      };
    },
    impairedSeries() {
      return {
        data: this.series.map((dataPoint, idx) => {
          return this.isPoint(this.series, "impaired", idx);
        }),
        name: "impaired",
        type: "line",
        areaStyle: {
          color: this.getColor("impaired"),
          opacity: 0.5,
        },
        lineStyle: { color: this.getColor("impaired") },
        color: this.getColor("impaired"),
      };
    },
    fluctuationSeries() {
      return {
        data: this.series.map((dataPoint, idx) => {
          return this.isPoint(this.series, "fluctuation", idx);
        }),
        name: "fluctuation",
        lineStyle: { color: this.getColor("fluctuation") },
        color: this.getColor("fluctuation"),
        type: "line",
        areaStyle: {
          color: this.getColor("fluctuation"),
          opacity: 0.5,
        },
      };
    },
    emptySeries() {
      return {
        data: this.series.map((dataPoint, idx) => {
          if (
            dataPoint.outageType !== "fluctuation" &&
            dataPoint.outageType !== "impaired"
          ) {
            return 0;
          }
          return null;
        }),
        type: "line",
        name: "white",
      };
    },
    /**
     * Converts the series data to an array of Dates
     * @returns {Date[]}
     */
    timeValues() {
      return this.series.map((dataPoint) => new Date(dataPoint.time));
    },
  },
  methods: {
    isPoint(arr, type, idx) {
      if (arr[idx].outageType === type) {
        return arr[idx].point;
      } else if (
        idx + 1 <= arr.length - 1 &&
        arr[idx + 1].outageType === type
      ) {
        return 0;
      } else if (idx !== 0 && arr[idx - 1].outageType === type) {
        return 0;
      } else return null;
    },
    chartFinished() {
      this.$emit(EMITTED_ACTIONS.FINISHED, true);
    },
    reloadChart() {
      if (this.$refs.chartInstance) {
        this.$refs.chartInstance.resize();
      }
    },
    chartOnClick(e) {},
    /**
     * Formats a Date into an easily-readable string
     * @param {Date} date
     * @returns {string}
     */
    formatDate(date) {
      return new dayjs.utc(date).format("MMM D");
    },
    formatHoverDate(date) {
      return new dayjs.utc(date).format("MMM D HH:00");
    },
    getColor(value) {
      const colors = {
        impaired: "#f8887a",
        fluctuation: "#f4f63d",
        white: "white",
      };

      return colors[value];
    },
  },
  watch: {
    refresh: {
      handler() {
        this.reloadChart();
      },
    },
  },
  beforeDestroy() {},
};
</script>

<style scoped>
/**
TODO: Change these styles when the fema panel is complete
*/
.power-outage-chart {
  color: #fff;
  padding: 0px !important;
}
/**
 * The default size is 600px×400px, for responsive charts
 * you may need to set percentage values as follows (also
 * don't forget to provide a size for the container).
 */
.echarts {
  width: 340px;
  height: 200px;
}
</style>
